/**
 * Reads an explicit boolean LH flag (e.g. `showPathFinding` / `showWayFinding`)
 * off an LH node (building or floor). Returns the boolean ONLY when it is a real
 * boolean; anything else (missing / null / undefined / non-boolean) yields
 * `undefined` so the caller treats that level as "inherit from parent" — never
 * as a disable. This is what guarantees a flag can't change behavior unless it
 * is explicitly set.
 */
const readExplicitFlag = (node, flagKey) => {
    if (node && typeof node === 'object') {
        const value = node[flagKey];
        if (typeof value === 'boolean')
            return value;
    }
    return undefined;
};
/**
 * Shared hierarchical resolver for boolean LH flags that cascade in the order:
 * tenant (base) -> building -> floor. There is intentionally NO room-level
 * check — a floor's resolved value applies to every room/desk on that floor.
 *
 * Only EXPLICIT booleans act. `null` / `undefined` / missing / non-boolean at a
 * level are ignored and inherit from the parent, so when the flag isn't present
 * the result is exactly today's behavior.
 *
 *   - tenant base is false        -> disabled (LH never consulted)
 *   - building flag is false      -> disabled for the whole building
 *   - floor flag is false         -> disabled for that floor only
 *   - otherwise                   -> enabled (inherited from the base)
 *
 * A lower level can only ever restrict: an explicit floor `true` cannot
 * re-enable a building that is explicitly `false`.
 */
const resolveHierarchicalFloorFlag = ({ location, floorNumber, flagKey, tenantEnabled, }) => {
    var _a;
    // Tenant/context gate (unchanged existing behavior).
    if (!tenantEnabled)
        return false;
    // Building level = the LH root object.
    if (readExplicitFlag(location, flagKey) === false)
        return false;
    // Floor level = the child whose floorNumber matches the destination floor.
    if (floorNumber !== null &&
        floorNumber !== undefined &&
        Number.isFinite(floorNumber)) {
        const floorNode = (_a = location === null || location === void 0 ? void 0 : location.children) === null || _a === void 0 ? void 0 : _a.find((child) => Number(child === null || child === void 0 ? void 0 : child.floorNumber) === Number(floorNumber));
        if (readExplicitFlag(floorNode, flagKey) === false)
            return false;
    }
    // Nothing in the chain explicitly disabled it -> inherit the enabled base.
    return true;
};
/**
 * Resolve whether path drawing is enabled for a given floor, honoring the
 * hierarchical LH `showPathFinding` flag (tenant base -> building -> floor).
 * The tenant base for path drawing is the wayfinding||pathfinding matrix.
 */
export const resolveShowPathFinding = ({ location, floorNumber, tenantPathDrawingEnabled, }) => resolveHierarchicalFloorFlag({
    location,
    floorNumber,
    flagKey: 'showPathFinding',
    tenantEnabled: tenantPathDrawingEnabled,
});
/**
 * Resolve whether wayfinding QR display is enabled for a given floor, honoring
 * the hierarchical LH `showWayFinding` flag (tenant base -> building -> floor).
 * The tenant base here is `isWayFindingEnabled` alone. When this resolves to
 * `false` the QR code must not be shown in the room details popup nor in the
 * floor-level "navigate on phone" widget for that floor.
 */
export const resolveShowWayFinding = ({ location, floorNumber, tenantWayFindingEnabled, }) => resolveHierarchicalFloorFlag({
    location,
    floorNumber,
    flagKey: 'showWayFinding',
    tenantEnabled: tenantWayFindingEnabled,
});
/**
 * Resolve whether desks are bookable/shown for a given floor, honoring the
 * hierarchical LH `deskBookable` flag in the order: building -> floor. There is
 * NO tenant level and NO room level for this flag; the default is enabled
 * (desks shown/bookable), so the tenant base is always `true`.
 *
 * Only EXPLICIT booleans act — `null` / `undefined` / missing / non-boolean at
 * a level inherit from the parent and never disable. When it resolves to
 * `false`, desks on that floor must not be highlighted, the desk popup must not
 * open, and the "Desks" selector tab is disabled.
 *
 *   - building `deskBookable` false -> disabled for the whole building
 *   - floor `deskBookable` false    -> disabled for that floor only
 *   - otherwise                     -> enabled (today's behavior)
 */
export const resolveDeskBookable = ({ location, floorNumber, }) => resolveHierarchicalFloorFlag({
    location,
    floorNumber,
    flagKey: 'deskBookable',
    tenantEnabled: true,
});
const SHOW_OCCUPANCY_FLAG = 'showOccupancy';
/** Find the LH floor node for a floor number (undefined if not resolvable). */
const findFloorNode = (location, floorNumber) => {
    var _a;
    if (floorNumber === null ||
        floorNumber === undefined ||
        !Number.isFinite(floorNumber)) {
        return undefined;
    }
    return (_a = location === null || location === void 0 ? void 0 : location.children) === null || _a === void 0 ? void 0 : _a.find((child) => Number(child === null || child === void 0 ? void 0 : child.floorNumber) === Number(floorNumber));
};
/**
 * Find an entity (room OR desk) node by richMapId within a floor node. Desks are
 * part of the "room level" tier, so we look through the floor's `rooms` array
 * (and a `desks` array if the payload ever carries one) — whichever holds the
 * matching richMapId. Returns undefined when the entity isn't an LH node (it
 * then inherits floor/building).
 */
const findEntityNodeOnFloor = (floorNode, richMapId) => {
    if (!floorNode || typeof floorNode !== 'object' || !richMapId) {
        return undefined;
    }
    const node = floorNode;
    const buckets = [node.rooms, node.desks];
    for (const bucket of buckets) {
        if (Array.isArray(bucket)) {
            const match = bucket.find((entity) => entity &&
                typeof entity === 'object' &&
                entity.richMapId === richMapId);
            if (match)
                return match;
        }
    }
    return undefined;
};
/**
 * Resolve whether occupancy/availability should be shown for a specific
 * room/desk, honoring the hierarchical LH `showOccupancy` flag in the order:
 * building -> floor -> room (desks are part of the room tier). There is NO
 * tenant level — occupancy is shown by default.
 *
 * Only EXPLICIT booleans act. `null` / `undefined` / missing / non-boolean at a
 * level inherit from the parent and never disable, so behavior is exactly
 * today's unless the flag is explicitly set to `false`. An explicit `true` at a
 * lower level cannot re-enable a level that is explicitly `false`.
 *
 *   - building `showOccupancy` false -> off for every room/desk in the building
 *   - floor `showOccupancy` false    -> off for every room/desk on that floor
 *   - room/desk `showOccupancy` false-> off for that room/desk only
 *   - otherwise                      -> on (today's behavior)
 *
 * When it resolves to `false`, the map highlight for that space is suppressed
 * and the availability status card (plus the widgets that ride with it) is
 * hidden in the details popup — the layout matches a space with no availability.
 */
export const resolveShowOccupancy = ({ location, floorNumber, richMapId, }) => {
    // Building level = the LH root object.
    if (readExplicitFlag(location, SHOW_OCCUPANCY_FLAG) === false)
        return false;
    // Floor level = the child whose floorNumber matches.
    const floorNode = findFloorNode(location, floorNumber);
    if (readExplicitFlag(floorNode, SHOW_OCCUPANCY_FLAG) === false)
        return false;
    // Room/desk level = the matching entity within that floor.
    const entityNode = findEntityNodeOnFloor(floorNode, richMapId);
    if (readExplicitFlag(entityNode, SHOW_OCCUPANCY_FLAG) === false)
        return false;
    return true;
};
/**
 * Build a fast per-floor `showOccupancy` predicate for map highlighting. The
 * building/floor levels are resolved once up front; the set of explicitly-`false`
 * room/desk richMapIds on the floor is precomputed so each per-space check is
 * O(1). This keeps the highlight hot path (which iterates every space every
 * pass) allocation- and scan-free after the initial build.
 *
 * Returns `(richMapId) => boolean`:
 *   - building or floor explicitly false -> always false
 *   - richMapId explicitly false         -> false
 *   - otherwise                          -> true (today's behavior)
 */
export const createShowOccupancyPredicateForFloor = (location, floorNumber) => {
    if (readExplicitFlag(location, SHOW_OCCUPANCY_FLAG) === false) {
        return () => false;
    }
    const floorNode = findFloorNode(location, floorNumber);
    if (readExplicitFlag(floorNode, SHOW_OCCUPANCY_FLAG) === false) {
        return () => false;
    }
    const hiddenRichMapIds = new Set();
    if (floorNode && typeof floorNode === 'object') {
        const node = floorNode;
        const buckets = [node.rooms, node.desks];
        for (const bucket of buckets) {
            if (!Array.isArray(bucket))
                continue;
            for (const entity of bucket) {
                if (readExplicitFlag(entity, SHOW_OCCUPANCY_FLAG) === false &&
                    entity &&
                    typeof entity === 'object') {
                    const id = entity.richMapId;
                    if (typeof id === 'string')
                        hiddenRichMapIds.add(id);
                }
            }
        }
    }
    if (hiddenRichMapIds.size === 0)
        return () => true;
    return (richMapId) => !hiddenRichMapIds.has(richMapId);
};
